import React from "react";
import {
  // shopping / retail
  ShoppingCart, ShoppingBasket, ShoppingBag, Croissant, Fish, Coffee, Leaf,
  Sandwich, Utensils, Drumstick, Gift, Laptop, Store, Building2, Wine,
  HeartPulse, Shirt, Sparkles, Baby, IceCream, Gamepad2, Dumbbell, Palette,
  PlaneTakeoff, Car, Wrench, PawPrint, Sofa, ChefHat, Luggage, BookOpen,
  // booking
  Calendar, Ticket, TreePalm, Home, Hotel,
  // ticket sub-types
  TrainFront, Bus, Plane, Ship, Clapperboard, FerrisWheel, Music, Trophy, Landmark,
  // classifieds
  Tag, User,
} from "lucide-react";

/* ---------- Types ---------- */
export interface CategoryItem {
  id: number;
  name: string;
  parentId?: number | null;
  color?: string;
  icon: React.ComponentType<{ size?: number; color?: string }>;
}

/* ==================================================================
   ID SCHEME — id = parentId * 100 + index, no gaps, no exceptions.
   Lineage is readable straight off the number:
     108   → parent 1   (Shopping)
     207   → parent 2   (Booking)
     20206 → parent 202 (Ticket) → parent 2 (Booking)

   HIERARCHY
     Shopping (1)    → storeType      → DONE
     Booking  (2)    → bookingType    → DONE
                          └ Ticket (202) → ticketType → DONE
     Classifieds (3) → classifiedType → DONE
   ================================================================== */

/* ---------- 0️⃣  Onboarding type — Business vs Individual ---------- */
export const onboardingTypes: CategoryItem[] = [
  { id: 1, name: "Business",   color: "#a67c00", icon: Building2 },
  { id: 2, name: "Individual", color: "#B91C1C", icon: User      },
];

/* ---------- 1️⃣  Top-level business types ---------- */
export const businessType: CategoryItem[] = [
  { id: 1, name: "Shopping",    color: "#0EA5E9", icon: ShoppingBag },
  { id: 2, name: "Booking",     color: "#a67c00", icon: Calendar    },
  { id: 3, name: "Classifieds", color: "#16a34a", icon: Tag         },
];

/* ==================================================================
   2️⃣  SHOPPING (parentId 1) — 101…132, flat
   ================================================================== */
export const storeType: CategoryItem[] = [
  // ── food & fresh ──
  { id: 101, parentId: 1, name: "Grocery Stores",     color: "#4CAF50", icon: ShoppingCart   },
  { id: 102, parentId: 1, name: "Fresh Markets",      color: "#E67E22", icon: ShoppingBasket },
  { id: 103, parentId: 1, name: "Bakeries",           color: "#FFB300", icon: Croissant      },
  { id: 104, parentId: 1, name: "Butcher Shops",      color: "#E53935", icon: Drumstick      },
  { id: 105, parentId: 1, name: "Seafood Markets",    color: "#03A9F4", icon: Fish           },
  { id: 106, parentId: 1, name: "Beverage Shops",     color: "#8E44AD", icon: Coffee         },
  { id: 107, parentId: 1, name: "Organic Stores",     color: "#43A047", icon: Leaf           },
  { id: 108, parentId: 1, name: "Fast Foods",         color: "#FF9800", icon: Sandwich       },

  // ── general retail ──
  { id: 109, parentId: 1, name: "Gifts",              color: "#E91E63", icon: Gift           },
  { id: 110, parentId: 1, name: "Electronics",        color: "#2196F3", icon: Laptop         },
  { id: 111, parentId: 1, name: "Furniture",          color: "#a855f7", icon: Sofa           },

  // ── stores ──
  { id: 112, parentId: 1, name: "Supermarket",        color: "#42A5F5", icon: ShoppingCart   },
  { id: 113, parentId: 1, name: "Hypermarket",        color: "#42A5F5", icon: Building2      },
  { id: 114, parentId: 1, name: "Convenience Store",  color: "#42A5F5", icon: ShoppingBasket },
  { id: 115, parentId: 1, name: "Department Store",   color: "#42A5F5", icon: Store          },
  { id: 116, parentId: 1, name: "Wholesale Club",     color: "#42A5F5", icon: Store          },
  { id: 117, parentId: 1, name: "Alcohol & Spirits",  color: "#7B1FA2", icon: Wine           },
  { id: 118, parentId: 1, name: "Pharmacy",           color: "#00897B", icon: HeartPulse     },
  { id: 119, parentId: 1, name: "Fashion Store",      color: "#B8860B", icon: Shirt          },
  { id: 120, parentId: 1, name: "Beauty Store",       color: "#CC2200", icon: Sparkles       },
  { id: 121, parentId: 1, name: "Baby Store",         color: "#F06292", icon: Baby           },
  { id: 122, parentId: 1, name: "Toy Store",          color: "#FF8F00", icon: IceCream       },
  { id: 123, parentId: 1, name: "Gaming Store",       color: "#6A1B9A", icon: Gamepad2       },
  { id: 124, parentId: 1, name: "Sports Store",       color: "#D32F2F", icon: Dumbbell       },
  { id: 125, parentId: 1, name: "Art & Crafts Store", color: "#F57C00", icon: Palette        },
  { id: 126, parentId: 1, name: "Aviation Store",     color: "#0288D1", icon: PlaneTakeoff   },
  { id: 127, parentId: 1, name: "Automotive Store",   color: "#C62828", icon: Car            },
  { id: 128, parentId: 1, name: "Tools Store",        color: "#4E342E", icon: Wrench         },
  { id: 129, parentId: 1, name: "Pet Store",          color: "#558B2F", icon: PawPrint       },
  { id: 130, parentId: 1, name: "Kitchen Store",      color: "#EF6C00", icon: ChefHat        },
  { id: 131, parentId: 1, name: "Luggage Store",      color: "#4527A0", icon: Luggage        },
  { id: 132, parentId: 1, name: "Book Store",         color: "#0059ff", icon: BookOpen       },
];

/* ==================================================================
   3️⃣  BOOKING (parentId 2) — 201…207
   Restaurants lands at 207 now that it's a Booking child.
   ================================================================== */
export const bookingType: CategoryItem[] = [
  { id: 201, parentId: 2, name: "Ride",             color: "#F57C00", icon: Car      },
  { id: 202, parentId: 2, name: "Ticket",           color: "#0EA5E9", icon: Ticket   },
  { id: 203, parentId: 2, name: "Vacation Rentals", color: "#a67c00", icon: TreePalm },
  { id: 204, parentId: 2, name: "Cleanings",        color: "#00897B", icon: Sparkles },
  { id: 205, parentId: 2, name: "Vacant Rentals",   color: "#2563eb", icon: Home     },
  { id: 206, parentId: 2, name: "Hotels",           color: "#8E44AD", icon: Hotel    },
  { id: 207, parentId: 2, name: "Restaurants",      color: "#FF7043", icon: Utensils },
];

/* ── Ticket (parentId 202) — 20201…20209 ── */
export const ticketType: CategoryItem[] = [
  { id: 20201, parentId: 202, name: "Train",         color: "#0EA5E9", icon: TrainFront   },
  { id: 20202, parentId: 202, name: "Bus & Coach",   color: "#F57C00", icon: Bus          },
  { id: 20203, parentId: 202, name: "Air",           color: "#2563eb", icon: Plane        },
  { id: 20204, parentId: 202, name: "Ferry & Boat",  color: "#00897B", icon: Ship         },
  { id: 20205, parentId: 202, name: "Cinema",        color: "#6A1B9A", icon: Clapperboard },
  { id: 20206, parentId: 202, name: "Theme Park",    color: "#E91E63", icon: FerrisWheel  },
  { id: 20207, parentId: 202, name: "Concerts",      color: "#BF360C", icon: Music        },
  { id: 20208, parentId: 202, name: "Sports Events", color: "#D32F2F", icon: Trophy       },
  { id: 20209, parentId: 202, name: "Attractions",   color: "#16a34a", icon: Landmark     },
];

/* ==================================================================
   4️⃣  CLASSIFIEDS (parentId 3) — 301…302, leaves
   ================================================================== */
export const classifiedType: CategoryItem[] = [
  { id: 301, parentId: 3, name: "Properties", color: "#16a34a", icon: Building2 },
  { id: 302, parentId: 3, name: "Automotive", color: "#C62828", icon: Car       },
];

/* ==================================================================
   5️⃣  FLAT INDEX — every non-top-level node in one array, so
   `businessSubcategories.find(s => s.id === saved)` works anywhere
   without knowing which branch the id came from.
   ================================================================== */
export const businessSubcategories: CategoryItem[] = [
  ...storeType,
  ...bookingType,
  ...ticketType,
  ...classifiedType,
];

/* ---------- 6️⃣  Helpers ---------- */

/** Children of any node — drives the next wizard step. */
export const getSubcategories = (parentId?: number | string | null): CategoryItem[] =>
  parentId == null ? [] : businessSubcategories.filter(s => s.parentId === Number(parentId));

/** True when a pick still needs one more step (currently only Ticket). */
export const hasSubcategories = (id?: number | string | null): boolean =>
  getSubcategories(id).length > 0;

/** Look up any node by id, top-level included. */
export const findCategory = (id?: number | string | null): CategoryItem | undefined =>
  id == null ? undefined : [...businessType, ...businessSubcategories].find(c => c.id === Number(id));

/** Breadcrumb from a leaf back up, e.g. Booking › Ticket › Theme Park */
export const getCategoryPath = (id?: number | string | null): CategoryItem[] => {
  const path: CategoryItem[] = [];
  let node = findCategory(id);
  while (node) {
    path.unshift(node);
    node = node.parentId ? findCategory(node.parentId) : undefined;
  }
  return path;
};

export const renderCategoryIcon = (
  IconComponent: React.ComponentType<{ size?: number; color?: string }>,
  color: string = "#555",
  size: number = 24
) => <IconComponent size={size} color={color} />;

/* ==================================================================
   PartnerCategories — single combined export
   ================================================================== */
export const PartnerCategories = {
  onboardingTypes,
  businessType,
  storeType,
  bookingType,
  ticketType,
  classifiedType,
  businessSubcategories,
  getSubcategories,
  hasSubcategories,
  findCategory,
  getCategoryPath,
};

export default PartnerCategories;